import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useAnalytics } from "../../../context/AnalyticsContext";

const PIE_COLORS = ["#10b981", "#3b82f6", "#f97316", "#a855f7", "#facc15"];

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { fetchOverviewAnalytics } = useAnalytics();

  useEffect(() => {
    let isMounted = true;

    const fetchAnalytics = async () => {
      try {
        const overview = await fetchOverviewAnalytics();
        if (!isMounted) return;
        setData(overview);
      } catch (err) {
        console.error("Failed to load analytics:", err);
        if (isMounted) {
          setError("Unable to load analytics data.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      isMounted = false;
    };
  }, [fetchOverviewAnalytics]);

  if (loading) {
    return (
      <div className="osh-analytics-page">
        <h2 className="osh-analytics-title">Traffic Analytics</h2>
        <p>Loading analytics…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="osh-analytics-page">
        <h2 className="osh-analytics-title">Traffic Analytics</h2>
        <p className="osh-analytics-error">{error || "No data available."}</p>
      </div>
    );
  }

  const {
    totalHits,
    humanHits,
    botHits,
    daily = [],
    paths = [],
    devices = [],
    browsers = [],
    countries = [],
  } = data;

  const humanRatio =
    totalHits > 0 ? Math.round((humanHits / totalHits) * 100) : 0;
  const botRatio = totalHits > 0 ? Math.round((botHits / totalHits) * 100) : 0;

  return (
    <div className="osh-analytics-page">
      <header className="osh-analytics-header">
        <h2 className="osh-analytics-title">Traffic Analytics</h2>
        <p className="osh-analytics-subtitle">
          Anonymous page hits from your OSH landing and login pages. No cookies,
          no tracking — just high-level usage insights.
        </p>
      </header>

      {/* Summary cards */}
      <section className="osh-analytics-cards">
        <div className="osh-analytics-card">
          <h3>Total page views</h3>
          <p className="osh-analytics-card-value">{totalHits ?? 0}</p>
          <p className="osh-analytics-card-note">
            All recorded page hits across tracked endpoints.
          </p>
        </div>

        <div className="osh-analytics-card">
          <h3>Human traffic</h3>
          <p className="osh-analytics-card-value">{humanHits ?? 0}</p>
          <p className="osh-analytics-card-note">
            ~{humanRatio}% of all hits.
          </p>
        </div>

        <div className="osh-analytics-card">
          <h3>Bot / crawler hits</h3>
          <p className="osh-analytics-card-value">{botHits ?? 0}</p>
          <p className="osh-analytics-card-note">
            ~{botRatio}% identified as bots.
          </p>
        </div>
      </section>

      <div className="osh-analytics-grid">
        {/* Daily traffic */}
        <section className="osh-analytics-panel">
          <h3>Page hits over time</h3>
          <p className="osh-analytics-panel-sub">
            Daily totals, split by human vs bot.
          </p>
          <div className="osh-analytics-chart">
            {daily.length === 0 ? (
              <p>No time-series data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="hits"
                    name="Total hits"
                    stroke="#3b82f6"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="humanHits"
                    name="Human"
                    stroke="#10b981"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="botHits"
                    name="Bots"
                    stroke="#f97316"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Top endpoints */}
        <section className="osh-analytics-panel">
          <h3>Top endpoints</h3>
          <p className="osh-analytics-panel-sub">
            Which tracked endpoints are hit the most.
          </p>
          <div className="osh-analytics-chart">
            {paths.length === 0 ? (
              <p>No endpoint data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={paths}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="path" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hits" name="Hits" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Device type */}
        <section className="osh-analytics-panel">
          <h3>Device types</h3>
          <p className="osh-analytics-panel-sub">
            Desktop vs mobile vs tablet (classified from User-Agent).
          </p>
          <div className="osh-analytics-chart">
            {devices.length === 0 ? (
              <p>No device data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={devices}
                    dataKey="hits"
                    nameKey="deviceType"
                    label
                    outerRadius={100}
                  >
                    {devices.map((entry, index) => (
                      <Cell
                        key={`device-${entry.deviceType}-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Browsers */}
        <section className="osh-analytics-panel">
          <h3>Browsers</h3>
          <p className="osh-analytics-panel-sub">
            High-level browser breakdown.
          </p>
          <div className="osh-analytics-chart">
            {browsers.length === 0 ? (
              <p>No browser data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={browsers}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="browser" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hits" name="Hits" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Countries */}
        <section className="osh-analytics-panel">
          <h3>Top countries</h3>
          <p className="osh-analytics-panel-sub">
            Approximate country based on anonymized IP.
          </p>
          <div className="osh-analytics-chart">
            {countries.length === 0 ? (
              <p>No country data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={countries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="countryCode" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hits" name="Hits" fill="#a855f7" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsPage;
